import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PhoneCall, Plus, Loader2, Users, Clock, Phone, CheckCircle2, Search, Calendar, MessageSquare, X, BellRing } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["Pending", "Called", "Message Sent", "Promised", "Paid", "No Response"];
const badge = { Pending: "bg-amber-100 text-amber-700", Called: "bg-blue-100 text-blue-600", "Message Sent": "bg-indigo-100 text-indigo-600", Promised: "bg-amber-100 text-amber-600", Paid: "bg-emerald-100 text-emerald-600", "No Response": "bg-rose-100 text-rose-600", "Follow Up": "bg-amber-100 text-amber-700", Overdue: "bg-rose-100 text-rose-600" };
const monthBadge = ["bg-amber-100 text-amber-700", "bg-orange-100 text-orange-700", "bg-rose-100 text-rose-700"];
const initials = (n) => (n || "").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function Stat({ icon: Icon, tint, label, value, sub, subClass }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tint}`}><Icon size={20} /></div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900 leading-tight truncate">{value}</p>
        {sub && <p className={`text-xs ${subClass || "text-slate-400"}`}>{sub}</p>}
      </div>
    </div>
  );
}

export default function FollowUp() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_followup");
  const [logs, setLogs] = useState(null);
  const [due, setDue] = useState([]);
  const [stats, setStats] = useState({});
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ student_id: "", status: "Called", remarks: "" });
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("all");
  const [detailTab, setDetailTab] = useState("history");
  const [clsFilter, setClsFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = () => {
    api.get("/followups").then((r) => setLogs(r.data));
    api.get("/due-fees").then((r) => { setDue(r.data); setSel((p) => p || r.data[0] || null); });
    api.get("/followup-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const latestLog = useMemo(() => {
    const m = {};
    (logs || []).forEach((l) => { if (!m[l.student_id]) m[l.student_id] = l; });
    return m;
  }, [logs]);

  const statusOf = (s) => { const log = latestLog[s.student_id]; if (log && log.status === "Paid") return "Paid"; if ((s.due_months || []).length >= 3) return "Overdue"; return log ? "Follow Up" : "Pending"; };
  const cat = (s) => { const n = (s.due_months || []).length; return { overdue: n >= 2, thisMonth: n === 1, oneTwo: n >= 1 && n <= 2, moreTwo: n >= 3 }; };

  const counts = useMemo(() => {
    let o = 0, t = 0, ot = 0, mt = 0;
    due.forEach((s) => { const c = cat(s); if (c.overdue) o++; if (c.thisMonth) t++; if (c.oneTwo) ot++; if (c.moreTwo) mt++; });
    return { all: due.length, overdue: o, thisMonth: t, oneTwo: ot, moreTwo: mt };
  }, [due]);

  const rows = due.filter((s) => {
    const c = cat(s);
    if (tab === "overdue" && !c.overdue) return false;
    if (tab === "thisMonth" && !c.thisMonth) return false;
    if (tab === "oneTwo" && !c.oneTwo) return false;
    if (tab === "moreTwo" && !c.moreTwo) return false;
    if (clsFilter !== "all" && s.class_name !== clsFilter) return false;
    if (statusFilter !== "all" && statusOf(s) !== statusFilter) return false;
    if (search.trim() && ![s.name, s.admission_number, s.phone].filter(Boolean).join(" ").toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });
  // Newest fee-promise date first; students without a promise fall to the bottom (by due amount)
  const sortedRows = [...rows].sort((a, b) => {
    const pa = a.promise_date || "", pb = b.promise_date || "";
    if (pa && pb) return pb.localeCompare(pa);
    if (pa) return -1;
    if (pb) return 1;
    return b.due - a.due;
  });

  const save = async () => {
    if (!f.student_id) { toast.error("Select a student"); return; }
    try { await api.post("/followups", f); toast.success("Follow-up added"); setOpen(false); setF({ student_id: "", status: "Called", remarks: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const patchPromise = async (student_id, date, amount) => {
    try {
      await api.patch(`/students/${student_id}/meta`, { promise_date: date, promise_amount: amount });
      setDue((prev) => prev.map((x) => (x.student_id === student_id ? { ...x, promise_date: date, promise_amount: amount } : x)));
      setSel((p) => (p && p.student_id === student_id ? { ...p, promise_date: date, promise_amount: amount } : p));
      toast.success("Fee promise updated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed to update promise"); }
  };
  const quickLog = async (student_id, status, remarks) => {
    try { await api.post("/followups", { student_id, status, remarks }); toast.success(`${status}`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const weekday = today.toLocaleDateString("en-US", { weekday: "long" });
  const selLogs = (logs || []).filter((l) => l.student_id === sel?.student_id);
  const classNames = Array.from(new Set(due.map((s) => s.class_name).filter(Boolean)));

  if (!logs) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-500 flex items-center justify-center text-xl font-bold">₹</div>
          <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Fees Follow Ups</h1>
            <p className="text-slate-500 text-sm">Track, remind and collect pending fees easily</p></div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
            <Calendar size={16} className="text-indigo-500" /><div className="leading-tight"><div className="text-sm font-semibold text-slate-800">{dateStr}</div><div className="text-[11px] text-slate-400">{weekday}</div></div>
          </div>
          {canManage && <Button onClick={() => { setF({ student_id: sel?.student_id || "", status: "Called", remarks: "" }); setOpen(true); }} className="gap-1.5 bg-emerald-500 hover:bg-emerald-600" data-testid="add-followup-btn"><Plus size={18} />Add Follow Up</Button>}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} tint="bg-rose-100 text-rose-500" label="Total Defaulters" value={counts.all} sub="pending students" />
        <Stat icon={Clock} tint="bg-amber-100 text-amber-500" label="Pending Amount" value={fmt(due.reduce((a, s) => a + s.due, 0))} sub={`From ${counts.all} students`} />
        <Stat icon={Phone} tint="bg-emerald-100 text-emerald-500" label="Follow Ups Today" value={stats.followups_today ?? 0} sub={`${stats.followups_today_completed ?? 0} completed`} subClass="text-emerald-600" />
        <Stat icon={CheckCircle2} tint="bg-violet-100 text-violet-500" label="Collected Today" value={fmt(stats.collected_today ?? 0)} sub={`From ${stats.collected_today_students ?? 0} students`} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <Select value={clsFilter} onValueChange={setClsFilter}><SelectTrigger className="w-40 h-9" data-testid="fu-class-filter"><SelectValue placeholder="All Classes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Classes</SelectItem>{classNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-36 h-9" data-testid="fu-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{["Pending", "Follow Up", "Overdue"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search by name, admission no. or phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fu-search" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 text-sm">
        {[["all", `All (${counts.all})`], ["overdue", `Overdue (${counts.overdue})`], ["thisMonth", `This Month (${counts.thisMonth})`], ["oneTwo", `1-2 Months (${counts.oneTwo})`], ["moreTwo", `More than 2 Months (${counts.moreTwo})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-lg font-medium ${tab === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`} data-testid={`fu-tab-${k}`}>{lbl}</button>
        ))}
      </div>

      {/* Content: table + details */}
      <div className="grid lg:grid-cols-5 gap-5">
        <div className={`${sel ? "lg:col-span-3" : "lg:col-span-5"} bg-white rounded-xl border border-slate-200 overflow-hidden`}>
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                <tr>{["#", "Student", "Class", "Due Months", "Due Amount", "Fees Promise", "Last Follow Up", "Status", "Action"].map((h) => <th key={h} className="px-3 py-2.5 text-left whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedRows.length === 0 ? <tr><td colSpan={9} className="py-14 text-center text-slate-400">No students match this view.</td></tr> :
                  sortedRows.map((s, i) => {
                    const log = latestLog[s.student_id];
                    const stt = statusOf(s);
                    return (
                      <tr key={s.student_id} onClick={() => { setSel(s); setDetailTab("history"); }} className={`cursor-pointer hover:bg-slate-50 ${sel?.student_id === s.student_id ? "bg-indigo-50/60" : ""}`} data-testid={`fu-row-${s.admission_number}`}>
                        <td className="px-3 py-2.5 text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold shrink-0">{initials(s.name)}</div>
                            <div><div className="font-medium text-slate-800 leading-tight">{s.name}</div><div className="text-[11px] text-slate-400">{s.admission_number}</div></div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{s.class_name}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1">{(s.due_months || []).length === 0 ? <span className="text-slate-300">—</span> : (s.due_months || []).map((m, idx) => <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${monthBadge[Math.min(idx, 2)]}`}>{m}</span>)}</div>
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-rose-600 tabular whitespace-nowrap">{fmt(s.due)}</td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>{canManage ? <PromiseCell s={s} onSave={(d, a) => patchPromise(s.student_id, d, a)} /> : <span className="text-xs text-slate-500">{s.promise_date ? <>{s.promise_date}{s.promise_amount ? ` · ${fmt(s.promise_amount)}` : ""}</> : <span className="text-slate-300">—</span>}</span>}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{log ? <><div>{(log.date || "").slice(0, 10)}</div><div className="text-slate-400">{log.status}</div></> : <span className="text-slate-300">No contact</span>}</td>
                        <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge[stt]}`}>{stt}</span></td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1.5">
                            {s.phone ? <a href={`tel:${s.phone}`} className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600" data-testid={`fu-call-${s.admission_number}`}><Phone size={14} /></a> : <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center" title="No phone"><Phone size={14} /></span>}
                            {s.phone ? <a href={`https://wa.me/${(s.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-lg bg-indigo-500 text-white flex items-center justify-center hover:bg-indigo-600" data-testid={`fu-msg-${s.admission_number}`}><MessageSquare size={14} /></a> : <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center" title="No phone"><MessageSquare size={14} /></span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details panel */}
        {sel && (
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 self-start" data-testid="fu-details">
          {(<>
            <div className="flex items-center justify-between mb-3"><h3 className="font-heading font-bold text-slate-900">Follow Up Details</h3><button onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button></div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg font-bold">{initials(sel.name)}</div>
              <div><div className="font-bold text-slate-900">{sel.name}</div><div className="text-xs text-slate-400">Admission No. {sel.admission_number}</div><div className="text-xs text-slate-400">Class: {sel.class_name}</div></div>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 space-y-1.5 text-sm mb-4">
              <div className="flex justify-between"><span className="text-slate-500">Total Due Amount</span><span className="font-bold text-rose-600">{fmt(sel.due)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Due Months</span><span className="font-medium text-slate-700">{(sel.due_months || []).join(", ") || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Last Follow Up</span><span className="font-medium text-slate-700">{latestLog[sel.student_id] ? `${(latestLog[sel.student_id].date || "").slice(0, 10)} (${latestLog[sel.student_id].status})` : "—"}</span></div>
            </div>
            {canManage && <Button onClick={() => { setF({ student_id: sel.student_id, status: "Paid", remarks: "" }); setOpen(true); }} className="w-full mb-3 bg-emerald-500 hover:bg-emerald-600" data-testid="fu-mark-collected">Mark as Collected</Button>}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <a href={`tel:${sel.phone}`} className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><Phone size={14} className="text-emerald-500" />Call</a>
              <a href={`https://wa.me/${(sel.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><MessageSquare size={14} className="text-indigo-500" />Message</a>
              <button onClick={() => quickLog(sel.student_id, "Message Sent", "Reminder sent")} disabled={!canManage} className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="fu-send-reminder"><BellRing size={14} className="text-violet-500" />Send Reminder</button>
            </div>
            <div className="flex gap-4 border-b border-slate-100 mb-3 text-sm">
              {[["history", "Follow Up History"], ["fee", "Fee Details"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setDetailTab(k)} className={`pb-2 font-medium ${detailTab === k ? "text-indigo-600 border-b-2 border-indigo-600" : "text-slate-400"}`} data-testid={`fu-detailtab-${k}`}>{lbl}</button>
              ))}
            </div>
            {detailTab === "history" ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {selLogs.length === 0 ? <p className="text-slate-400 text-sm py-3"><PhoneCall size={18} className="inline mr-1 opacity-40" />No history yet.</p> :
                  selLogs.map((l) => (
                    <div key={l.id} className="flex gap-2 text-sm">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><PhoneCall size={13} className="text-slate-500" /></div>
                      <div><div className="flex items-center gap-2"><span className="text-xs text-slate-500">{(l.date || "").slice(0, 10)}</span><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span></div><div className="text-sm text-slate-700">{l.remarks}</div><div className="text-[11px] text-slate-400">By: {l.followed_by}</div></div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Total Payable</span><span className="font-medium">{fmt(sel.total_payable)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-medium text-emerald-600">{fmt(sel.paid)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-medium">{fmt(sel.discount)}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="text-slate-500">Due</span><span className="font-bold text-rose-600">{fmt(sel.due)}</span></div>
                {sel.last_paid_date && <div className="flex justify-between"><span className="text-slate-500">Last Paid</span><span className="font-medium">{sel.last_paid_date} ({fmt(sel.last_paid_amount)})</span></div>}
              </div>
            )}
          </>)}
        </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Follow-up</DialogTitle><DialogDescription>Record a call, message or payment promise for a student.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student</Label>
              <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}><SelectTrigger data-testid="followup-student"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{due.map((s) => <SelectItem key={s.student_id} value={s.student_id}>{s.name} ({fmt(s.due)})</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="followup-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Remarks</Label><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="followup-remarks" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-followup-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromiseCell({ s, onSave }) {
  const [date, setDate] = useState(s.promise_date || "");
  const [amount, setAmount] = useState(s.promise_amount ?? "");
  useEffect(() => { setDate(s.promise_date || ""); setAmount(s.promise_amount ?? ""); }, [s.promise_date, s.promise_amount]);
  const save = () => {
    if ((date || "") !== (s.promise_date || "") || Number(amount || 0) !== Number(s.promise_amount || 0))
      onSave(date || "", Number(amount || 0));
  };
  const overdue = date && date < new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col gap-1 w-32" data-testid={`fu-promise-cell-${s.admission_number}`}>
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={save} className={`h-7 text-xs px-1.5 ${overdue ? "border-rose-400 text-rose-600" : ""}`} data-testid={`fu-promise-date-${s.admission_number}`} />
      <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={save} className="h-7 text-xs px-1.5 tabular" data-testid={`fu-promise-amount-${s.admission_number}`} />
    </div>
  );
}
